import React, { ChangeEvent, useEffect, useState } from 'react';
import { CustomImageHandler } from '../../../../../../../shared/components/CustomImageHandler';
import { FormControl, Image, InputGroup } from 'react-bootstrap';
import { numberFormatter } from '../../../../../../../utils/helpers/numberFormater';
import Counter from '../../../../../../../shared/components/Counter';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { IAllStates } from '../../../../../../../store/rootReducer';
import {
    calculateAvgReach,
    checkNumberPosts,
    checkPrice,
    getCpmValue,
    getOldPricePerPlatform,
    getPostsPerPlatform,
    getPriceOneContent,
    getPricePerPlatform,
} from '../../../../../../../shared/functions/Functions';
import { TrashIcon } from '../../../../../../../shared/Icons';
import { setContractItems } from '../../../../../../../store/influencer/contractInfluencerSlice';
import { priceSplitter } from '../../../../../../../utils/helpers/priceSplitter';
import { ITotalPlatforms } from '../../../../../../../model/platform/ITotalPlatforms';
import Flag from '../../../../../../../shared/components/flag/Flag';

interface IInitialPlatformProps {
    platform: ITotalPlatforms;
}

export function InitialContractPlatform({ platform }: IInitialPlatformProps) {
    const { initialContract, contractModalStatus, isCharity } = useSelector(
        (state: IAllStates) => state.contractInfluencer
    );
    const [expended, setExpended] = useState(true);
    const { t } = useTranslation();
    const dispatch = useDispatch();

    useEffect(() => {
        setExpended(contractModalStatus === 'negotiate' || window.innerWidth <= 600);
    }, [contractModalStatus]);

    function deleteContractItem(socialPlatformId: number) {
        const initialContractItemCopy = [...initialContract.contractItems];
        const selectedContractItem = initialContractItemCopy.find(item => item.socialPlatformId === socialPlatformId);
        const index = selectedContractItem ? initialContractItemCopy.indexOf(selectedContractItem) : 0;
        initialContractItemCopy[index] = { ...initialContractItemCopy[index], nbPosts: 0, price: 0 };
        dispatch(setContractItems(initialContractItemCopy));
    }

    function changeHandlerContractItem(
        socialPlatformId: number,
        event?: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        nbPosts?: number
    ) {
        const value = event && Number(event.target.value);
        const initialContractItemCopy = [...initialContract.contractItems];
        const selectedContractItem = initialContractItemCopy.find(item => item.socialPlatformId === socialPlatformId);
        const index = selectedContractItem ? initialContractItemCopy.indexOf(selectedContractItem) : 0;
        if (selectedContractItem) {
            if (nbPosts === 0) {
                deleteContractItem(socialPlatformId);
            } else {
                initialContractItemCopy[index] = {
                    ...initialContractItemCopy[index],
                    price: value === undefined ? initialContractItemCopy[index].price : value,
                    nbPosts: nbPosts === undefined ? initialContractItemCopy[index].nbPosts : nbPosts,
                };
                dispatch(setContractItems(initialContractItemCopy));
            }
        }
    }

    const isStrikeTrough = getPostsPerPlatform(initialContract.contractItems, platform.id) === 0;
    return (
        <>
            <tr
                onClick={() => setExpended(!expended)}
                className={`cursor-pointer ${isStrikeTrough ? 'text-line-through' : ''}`}
            >
                <td>
                    <div className="d-flex align-items-center">
                        <CustomImageHandler altTag={platform?.name ?? ''} photoPath={platform?.path ?? null} />
                        <span
                            className={`text-primary font-weight-bold pl-1 mb-0 ${
                                isStrikeTrough ? 'text-line-through' : ''
                            }`}
                        >
                            x
                            {contractModalStatus === 'preview'
                                ? platform.nbPostsTotal
                                : getPostsPerPlatform(initialContract.contractItems, platform.id)}
                        </span>
                        {expended ? (
                            <Image className="ml-2" alt="image-down" src="/icons/img_arrow_up.svg" />
                        ) : (
                            <Image className="ml-2" alt="image-down" src="/icons/img_arrow_down.svg" />
                        )}
                    </div>
                </td>
                <td className="d-none d-md-table-cell text-center">
                    <p>{numberFormatter(platform.totalFollowers)}</p>
                </td>
                <td className="d-none d-md-table-cell text-center">
                    <p>{numberFormatter(calculateAvgReach(initialContract.contractItems, platform))}</p>
                </td>
                <td className="d-none d-md-table-cell">
                    <div className="d-flex justify-content-center">
                        {platform?.audiences.map(country => (
                            <div key={country.id} className="d-flex pl-2 align-items-center">
                                <Flag className="rounded" code={country?.metric} width={26} />
                                <div className="d-flex flex-column pl-2">
                                    <p className="text-primary font-weight-bold mb-0">{Math.round(country.value)}%</p>
                                    <p className="text-secondary mb-0">{numberFormatter(country.followers)}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </td>
                <td className={`${isCharity ? 'd-none' : 'd-none d-md-table-cell text-center'}`}>
                    <p className="euro-symbol">{getCpmValue(initialContract.contractItems, platform)} </p>
                </td>
                <td className={`${isCharity ? 'd-none' : 'text-end d-none d-md-table-cell'}`}>
                    <div className="d-flex align-items-center justify-content-end">
                        {getOldPricePerPlatform(initialContract.contractItems, platform.id) > 0 && (
                            <p className="euro-symbol text-line-through mr-2">
                                {priceSplitter(
                                    Math.round(
                                        Math.round(getOldPricePerPlatform(initialContract.contractItems, platform.id))
                                    )
                                )}
                            </p>
                        )}
                        <p className="euro-symbol font-weight-semi-bold">
                            {priceSplitter(Math.round(getPricePerPlatform(initialContract.contractItems, platform.id)))}
                        </p>
                    </div>
                </td>
            </tr>
            {platform?.contractItems?.map(item => {
                if (!item || !item.socialPlatform || !item.socialPlatform.id) {
                    return;
                }

                const defaultPosts = item?.nbPosts ?? 0;
                const price = checkPrice(item.socialPlatform.id, item.price, initialContract?.contractItems ?? []);

                const postNumber = checkNumberPosts(
                    item.socialPlatform.id,
                    defaultPosts,
                    initialContract?.contractItems ?? []
                );

                return (
                    <tr
                        key={item.id}
                        className={`position-relative
                    ${postNumber === 0 || (!item.id && contractModalStatus === 'preview') ? 'text-line-through' : ''}
                    ${expended ? '' : 'd-none'}
                `}
                    >
                        <td className="vertical-align-td" colSpan={2}>
                            <div className="d-flex align-items-center pl-md-2">
                                {contractModalStatus === 'negotiate' && (
                                    <span className="mb-0" onClick={() => deleteContractItem(item.socialPlatform.id)}>
                                        <TrashIcon width="15" height="16" classes="cursor-pointer mr-2" />
                                    </span>
                                )}
                                <CustomImageHandler
                                    altTag={item?.socialPlatform?.name ?? ''}
                                    photoPath={item?.socialPlatform?.path ?? ''}
                                    classes="d-none d-md-flex"
                                />
                                <span className="px-md-2 mb-0">
                                    {t(`general.platforms.${item?.socialPlatform?.name}`)}
                                </span>
                            </div>
                        </td>
                        <td>
                            <div className="mr-md-3">
                                {contractModalStatus === 'negotiate' ? (
                                    <Counter
                                        defaultCount={postNumber}
                                        setNumberItems={changeHandlerContractItem}
                                        id={item.socialPlatform.id}
                                        itemId={item?.socialPlatform?.id}
                                    />
                                ) : (
                                    <span className="text-muted font-weight-bold mb-0">x {item?.nbPosts}</span>
                                )}
                            </div>
                        </td>
                        <td className={`${isCharity ? 'd-none' : 'd-none d-md-table-cell text-center'}`}>
                            <p className="text-muted mb-0 ">
                                {t('influencer.offer_management.modal.price_of_one_content')}
                                <span className="euro-symbol">
                                    {getPriceOneContent(initialContract.contractItems, item.socialPlatform.id)}
                                </span>
                            </p>
                        </td>
                        <td className={`${isCharity ? 'd-none' : 'd-none d-md-table-cell'}`}></td>
                        <td className={`${isCharity ? 'd-none' : 'd-flex justify-content-end'}`}>
                            {contractModalStatus === 'negotiate' ? (
                                <InputGroup id="price-input">
                                    <InputGroup.Text>€</InputGroup.Text>
                                    <FormControl
                                        min={0}
                                        onChange={(event: any) =>
                                            changeHandlerContractItem(item.socialPlatform.id, event, undefined)
                                        }
                                        type="number"
                                        placeholder="0"
                                        disabled={postNumber < 1}
                                        required={isCharity ? false : postNumber > 0}
                                        value={price === 0 ? '' : price}
                                        name="price"
                                        id="price-input"
                                        onKeyPress={(event: any) =>
                                            (event?.key === '-' || event?.key === '+') && event.preventDefault()
                                        }
                                    />
                                </InputGroup>
                            ) : (
                                <p className="text-muted mb-0 mt-md-2 euro-symbol font-weight-semi-bold">
                                    {priceSplitter(Math.round(item?.price ?? 0))}
                                </p>
                            )}
                        </td>
                    </tr>
                );
            })}
        </>
    );
}
